import { lazy, Suspense, useEffect, useState } from "react";
import type { MaterialListItem } from "@edu/shared";
import { externalCallHost } from "@edu/shared";
import { Empty } from "../../lib/Empty.tsx";
import { Loading } from "../../lib/loading.tsx";
import { absText, dayText } from "../../lib/when.ts";
import { materialSourceText, milestoneText, receiptText, revokedText } from "./material-text.ts";
import { useStudentMaterials } from "./use-student-materials.ts";

/**
 * Окно отправки ленивым чанком: внутри него выбор файла из медиатеки со своим
 * деревом папок, а карточку ученика открывают десятки раз в день затем, чтобы
 * посмотреть, а не отправить.
 */
const MaterialDialog = lazy(() =>
  import("./MaterialDialog.tsx").then((m) => ({ default: m.MaterialDialog })),
);

/**
 * Ссылки и файлы, отправленные ученику, с отметкой об открытии
 * (`docs/material-receipt-design.md`, часть А, шаг 2).
 *
 * ⚠️ **Три правила экрана, все из раздела 14 проекта:**
 *
 * 1. состояние безлично («Открыт 14 сентября», «Не открыт»), слова считает
 *    `material-text.ts`, а не разметка;
 * 2. **ни один статус не красится**: «Не открыт» стоит тем же тоном, что
 *    «Открыт». Первая же реализация захочет покрасить его красным, и это то же
 *    правило, что у счётчика долга (решение владельца 21.08.2026);
 * 3. **счёта в заголовке нет** («отправлено 12, открыто 3»): это счёт работы
 *    преподавателя, запрещённый решением владельца 23.08.2026. Поэтому у
 *    заголовка карточки, в отличие от соседних «Занятий», числа нет вовсе.
 *
 * ⚠️ **Квитанция в строке только этого ученика**, хотя сервер отдаёт всех
 * получателей материала. Отсюда отправляется всегда одному, и чужих строк в
 * списке не бывает, пока групповая отправка не появится в окне переписки
 * (раздел 8). В тот день решать, показывать ли здесь остальных поимённо
 * (развилка 17.1 закрыта для карточки материала, а не для карточки ученика).
 *
 * ⚠️ **Кнопки «копировать ссылку» нет** (раздел 12, шаг 2): без части Б
 * скопированный адрес вёл бы в никуда.
 */
export function StudentMaterials(props: {
  studentUserId: string;
  studentName: string;
  /** Отвязанному отправить нельзя: сервер ответит `409 student_not_linked`. */
  unlinked: boolean;
  now: number;
}) {
  const list = useStudentMaterials(props.studentUserId);
  const [open, setOpen] = useState(false);
  /** Строка, у которой нажат первый шаг отзыва или удаления. */
  const [asking, setAsking] = useState<{ id: string; what: "revoke" | "delete" } | null>(null);

  const items = list.items;
  const canSend = !props.unlinked;

  return (
    <section className="card">
      <h2>Ссылки и файлы</h2>

      {list.err !== null && <p className="err">{list.err}</p>}

      {items === null ? (
        list.err === null ? (
          <Loading />
        ) : (
          /* Первая загрузка упала: списка нет вовсе, и без кнопки выйти из
             этого можно только перезагрузкой всей страницы */
          <p className="acts">
            <button type="button" className="btn" disabled={list.busy} onClick={list.reload}>
              Попробовать ещё раз
            </button>
          </p>
        )
      ) : items.length === 0 ? (
        <Empty
          icon="folder"
          title="Ссылок и файлов пока нет"
          text={
            canSend
              ? "Отправьте ученику ссылку на видео или файл из материалов. Здесь будет видно, открывал ли их ученик."
              : UNLINKED_TEXT
          }
          {...(canSend
            ? { action: { label: "Отправить ссылку или файл", onClick: () => setOpen(true) } }
            : {})}
        />
      ) : (
        <>
          {/* Кнопка не пропадает молча: у отвязанного на её месте та же
              строка, что в пустом состоянии */}
          {canSend ? (
            <p className="acts">
              <button type="button" className="btn" onClick={() => setOpen(true)}>
                Отправить ссылку или файл
              </button>
            </p>
          ) : (
            <p className="muted">{UNLINKED_TEXT}</p>
          )}

          <ul className="list list--stu-mat">
            <li className="list__head" aria-hidden="true">
              <span>Отправлено</span>
              <span>Название</span>
              <span>Отметка</span>
              <span />
            </li>
            {items.map((m) => (
              <MaterialRow
                key={m.id}
                item={m}
                studentUserId={props.studentUserId}
                now={props.now}
                asking={asking?.id === m.id ? asking.what : null}
                busy={list.acting !== null}
                onAsk={(what) => setAsking(what === null ? null : { id: m.id, what })}
                onRevoke={() => {
                  setAsking(null);
                  list.revoke(m.id);
                }}
                onDelete={() => {
                  setAsking(null);
                  list.remove(m.id);
                }}
              />
            ))}
          </ul>

          {list.nextCursor !== null && (
            <p className="acts">
              <button className="btn" disabled={list.busy} onClick={list.loadMore}>
                {list.busy ? "Загрузка…" : "Показать ещё"}
              </button>
            </p>
          )}

          {/*
            Последствия стоят ЗДЕСЬ, до первого нажатия, а не в подтверждении:
            тот же приём, что у отзыва ссылки в «Доступе». Различие двух
            кнопок человек обязан понимать, ещё когда выбирает, какую нажать.
          */}
          <p className="hint">
            Отозванное ученик больше не видит, а отметка об открытии остаётся здесь. Удалённое
            пропадает совсем, вместе с отметкой. У видео и аудио под отметкой видно, примерно
            докуда дошло воспроизведение.
          </p>
        </>
      )}

      {open && (
        <Suspense fallback={null}>
          <MaterialDialog
            studentUserId={props.studentUserId}
            studentName={props.studentName}
            onClose={() => setOpen(false)}
            onSent={list.reload}
          />
        </Suspense>
      )}
    </section>
  );
}

/** Почему кнопки отправки нет. Одна строка на пустой и на непустой список. */
const UNLINKED_TEXT = "Ученик отвязан, отправить ничего нельзя.";

/**
 * Сколько подтверждение не принимает нажатий после появления. Больше интервала
 * двойного щелчка (у систем по умолчанию до 500 мс, обычно меньше) и заметно
 * меньше времени, за которое человек успевает прочитать «Да, удалить».
 */
const CONFIRM_ARM_MS = 600;

function MaterialRow(props: {
  item: MaterialListItem;
  studentUserId: string;
  now: number;
  asking: "revoke" | "delete" | null;
  busy: boolean;
  onAsk: (what: "revoke" | "delete" | null) => void;
  onRevoke: () => void;
  onDelete: () => void;
}) {
  const m = props.item;
  /** Подтверждение взведено: прошло `CONFIRM_ARM_MS` с его появления. */
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    setArmed(false);
    if (props.asking === null) return;
    const timer = window.setTimeout(() => setArmed(true), CONFIRM_ARM_MS);
    return () => window.clearTimeout(timer);
  }, [props.asking]);
  const mine = m.receipts.find((r) => r.studentUserId === props.studentUserId);
  const opened = mine?.firstOpenedAt ?? null;
  const reached = m.kind === "file" ? milestoneText(mine?.milestone) : null;
  /*
    Ссылкой название становится только у разбираемого адреса: домен рядом с
    ним обязан быть (раздел 8 проекта), а у неразбираемого показать нечего.
    `noreferrer` не приличия, а та же осторожность, что у ссылки на созвон.
  */
  const href = m.kind === "link" && externalCallHost(m.url) !== null ? m.url : null;

  return (
    <li className="row" data-dim={m.revokedAt !== null}>
      <span className="cell row__when" data-label="Отправлено" title={absText(m.createdAt)}>
        {dayText(m.createdAt, props.now)}
      </span>

      <span className="cell row__name">
        {href !== null ? (
          <a className="textlink" href={href} target="_blank" rel="noopener noreferrer">
            {m.title}
          </a>
        ) : (
          m.title
        )}
        <span className="row__sub">
          {materialSourceText(m)}
          {m.revokedAt !== null && ` · ${revokedText(m.revokedAt, props.now)}`}
        </span>
      </span>

      {/* Без тона в любом состоянии: раздел 14 проекта, шапка компонента.
          Веха второй строкой и только у файла, словами раздела 6.2 */}
      <span className="cell" title={opened === null ? undefined : absText(opened)}>
        {receiptText(mine, props.now)}
        {reached !== null && <span className="row__sub">{reached}</span>}
      </span>

      <span className="cell row__act">
        {props.asking === null ? (
          <>
            {m.revokedAt === null && (
              <button
                type="button"
                className="btn btn--sm"
                disabled={props.busy}
                onClick={() => props.onAsk("revoke")}
              >
                Отозвать
              </button>
            )}
            <button
              type="button"
              className="btn btn--sm"
              disabled={props.busy}
              onClick={() => props.onAsk("delete")}
            >
              Удалить
            </button>
          </>
        ) : (
          <>
            {/*
              Шаг подтверждения: следующее нажатие необратимо, и вес это
              показывает заливкой, а отмена тише подтверждения.

              ⚠️ **Двойной щелчок не должен проходить мимо подтверждения.** Если
              «Да, …» встаёт туда, где была первая кнопка, второй щелчок того же
              двойного нажатия попадает в него. Защита двойная, потому что одной
              раскладкой её не обеспечить: ячейка прижата вправо на широком
              экране и влево на узком, а подписи у кнопок разной ширины, то есть
              «на месте первой кнопки» оказывается разное на двух раскладках.
              Поэтому «Отмена» стоит первой, а «Да, …» не принимает нажатий
              первые `CONFIRM_ARM_MS` после появления: второй щелчок двойного
              нажатия приходит раньше.
            */}
            <button
              type="button"
              className="btn btn--sm btn--quiet"
              disabled={props.busy}
              onClick={() => props.onAsk(null)}
            >
              Отмена
            </button>
            <button
              type="button"
              className="btn btn--sm btn--danger is-confirm"
              disabled={props.busy || !armed}
              onClick={props.asking === "revoke" ? props.onRevoke : props.onDelete}
            >
              {props.asking === "revoke" ? "Да, отозвать" : "Да, удалить"}
            </button>
          </>
        )}
      </span>
    </li>
  );
}
